/**
 * Bank Withdrawal Management - Main Component (Refactored)
 * Split into: WithdrawalStats, WithdrawalFilters, WithdrawalTable, useWithdrawalManagement
 */
import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../ui/dialog';
import { Plus, RefreshCw, Banknote } from 'lucide-react';

// Import sub-components
import { useWithdrawalManagement } from './bank/useWithdrawalManagement';
import { WithdrawalStats } from './bank/WithdrawalStats';
import { WithdrawalFilters } from './bank/WithdrawalFilters';
import { WithdrawalTable } from './bank/WithdrawalTable';

const BankWithdrawalManagement = () => {
  const {
    withdrawals,
    bankLinks,
    loading,
    stats,
    filters,
    setFilters,
    fetchWithdrawals,
    handleApprove,
    handleCancel,
    handleRefund,
    handleCreate
  } = useWithdrawalManagement();

  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [newWithdrawal, setNewWithdrawal] = useState({
    user_id: '',
    bank_link_id: '',
    amount: '',
    reason: '',
    fee_rate: 0.029
  });

  const onCreateSubmit = async () => {
    if (!newWithdrawal.user_id || !newWithdrawal.amount) {
      return;
    }
    
    const success = await handleCreate(newWithdrawal);
    if (success) {
      setNewWithdrawal({ user_id: '', bank_link_id: '', amount: '', reason: '', fee_rate: 0.029 });
      setShowCreateDialog(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-white flex items-center gap-2">
            <Banknote className="w-6 h-6 text-green-400" />
            Bank Withdrawal Management
          </h2>
          <p className="text-gray-400 mt-1">Manage ACH bank withdrawals via Clear</p>
        </div>
        
        <div className="flex gap-2">
          <Button
            variant="outline"
            onClick={fetchWithdrawals}
            disabled={loading}
            className="border-gray-600 text-gray-300 hover:bg-gray-800"
          >
            <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
          
          <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
            <DialogTrigger asChild>
              <Button className="bg-green-600 hover:bg-green-700">
                <Plus className="w-4 h-4 mr-2" />
                Create Withdrawal
              </Button>
            </DialogTrigger>
            <DialogContent className="bg-gray-900 border-gray-700 max-w-md">
              <DialogHeader>
                <DialogTitle className="text-white">Create New Withdrawal</DialogTitle>
              </DialogHeader>
              <div className="space-y-4 mt-4">
                <div>
                  <Label className="text-gray-300">User ID</Label>
                  <Input
                    value={newWithdrawal.user_id}
                    onChange={(e) => setNewWithdrawal(prev => ({ ...prev, user_id: e.target.value }))}
                    placeholder="Enter user ID"
                    className="bg-gray-800 border-gray-700 text-white mt-1"
                  />
                </div>
                
                <div>
                  <Label className="text-gray-300">Bank Link (Optional)</Label>
                  <Select
                    value={newWithdrawal.bank_link_id}
                    onValueChange={(value) => setNewWithdrawal(prev => ({ ...prev, bank_link_id: value }))}
                  >
                    <SelectTrigger className="bg-gray-800 border-gray-700 text-white mt-1">
                      <SelectValue placeholder="Select bank link" />
                    </SelectTrigger>
                    <SelectContent className="bg-gray-800 border-gray-700">
                      <SelectItem value="">No specific bank link</SelectItem>
                      {bankLinks.map(link => (
                        <SelectItem key={link.id} value={link.id}>
                          {link.bank_name} - ****{link.account_last_four}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                
                <div>
                  <Label className="text-gray-300">Amount ($)</Label>
                  <Input
                    type="number"
                    step="0.01"
                    min="0"
                    value={newWithdrawal.amount}
                    onChange={(e) => setNewWithdrawal(prev => ({ ...prev, amount: e.target.value }))}
                    placeholder="0.00"
                    className="bg-gray-800 border-gray-700 text-white mt-1"
                  />
                </div>
                
                <div>
                  <Label className="text-gray-300">Fee Rate</Label>
                  <Input
                    type="number"
                    step="0.001"
                    min="0"
                    max="1"
                    value={newWithdrawal.fee_rate}
                    onChange={(e) => setNewWithdrawal(prev => ({ ...prev, fee_rate: parseFloat(e.target.value) }))}
                    className="bg-gray-800 border-gray-700 text-white mt-1"
                  />
                  <p className="text-gray-500 text-xs mt-1">
                    Fee: ${(parseFloat(newWithdrawal.amount || 0) * newWithdrawal.fee_rate).toFixed(2)}
                  </p>
                </div>
                
                <div>
                  <Label className="text-gray-300">Reason (Optional)</Label>
                  <Input
                    value={newWithdrawal.reason}
                    onChange={(e) => setNewWithdrawal(prev => ({ ...prev, reason: e.target.value }))}
                    placeholder="Withdrawal reason"
                    className="bg-gray-800 border-gray-700 text-white mt-1"
                  />
                </div>
                
                <Button
                  onClick={onCreateSubmit}
                  disabled={loading || !newWithdrawal.user_id || !newWithdrawal.amount}
                  className="w-full bg-green-600 hover:bg-green-700"
                >
                  {loading ? 'Creating...' : 'Create Withdrawal'}
                </Button>
              </div>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {/* Stats Cards */}
      <WithdrawalStats stats={stats} />

      {/* Filters */}
      <WithdrawalFilters filters={filters} setFilters={setFilters} />

      {/* Withdrawal Table */}
      <WithdrawalTable
        withdrawals={withdrawals}
        loading={loading}
        onApprove={handleApprove}
        onCancel={handleCancel}
        onRefund={handleRefund}
      />
    </div>
  );
};

export default BankWithdrawalManagement;
